import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
} from "@mui/material";
import { ChevronDown, ChevronUp, Edit, AlertTriangle } from "lucide-react";
import { infoToast } from "../../../../../services/toast.service";

const ProductVariantsInventory = ({ productData }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  // Generate variants based on product size if available
  const createVariantsFromProductData = () => {
    const defaultVariant = {
      id: 1,
      size: "Free Size",
      color: "Default",
      mrp: 800,
      discount: 25,
      sellingPrice: 600,
      inventory: defaultInventory,
    };

    // If no product data is provided, return sample variant
    if (!productData) {
      return [defaultVariant];
    }

    // Extract size information from product data
    let sizes = [];
    if (productData.size && typeof productData.size === "string") {
      sizes = productData.size.split(",").map(s => s.trim());
    }

    // If no size information, use default variant with product data
    if (sizes.length === 0) {
      return [
        {
          id: 1,
          size: productData.size || "Free Size",
          color: productData.color || "Default",
          mrp: productData.mrp || 800,
          discount:
            productData.mrp && productData.sellingPrice
              ? Math.round(
                  ((productData.mrp - productData.sellingPrice) /
                    productData.mrp) *
                    100,
                )
              : 25,
          sellingPrice: productData.sellingPrice || 600,
          inventory: {
            ...defaultInventory,
            total: productData.quantity || defaultInventory.total,
            available: productData.quantity || defaultInventory.available,
          },
        },
      ];
    }

    // Create variants based on sizes
    return sizes.map((size, index) => ({
      id: index + 1,
      size: size,
      color: productData.color || "Default",
      mrp: productData.mrp || 800,
      discount:
        productData.mrp && productData.sellingPrice
          ? Math.round(
              ((productData.mrp - productData.sellingPrice) / productData.mrp) *
                100,
            )
          : 25,
      sellingPrice: productData.sellingPrice || 600,
      inventory: {
        ...defaultInventory,
        total:
          Math.round(productData.quantity / sizes.length) ||
          defaultInventory.total,
        available:
          Math.round(productData.quantity / sizes.length) ||
          defaultInventory.available,
      },
    }));
  };

  // Default inventory data
  const defaultInventory = {
    total: 15,
    available: 12,
    reserved: 2,
    damaged: 1,
    batches: [
      { id: "B001", quantity: 8, manufacturingDate: "2024-01-15" },
      { id: "B002", quantity: 7, manufacturingDate: "2024-02-01" },
    ],
    warehouses: [
      { name: "Main Warehouse", quantity: 10 },
      { name: "North Warehouse", quantity: 5 },
    ],
  };

  // Use product data to generate variants
  const variants = createVariantsFromProductData();

  const renderInventoryDetails = inventory => (
    <div className="px-4 py-5 space-y-6 bg-white border-t border-gray-200">
      {/* Stock Status */}
      <div>
        <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-100">
          Stock Status
        </h4>
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-[#F9F8FE] border border-[#EFEDFB] p-3 rounded-md shadow-sm">
            <div className="text-sm text-[#5652B0]">Total</div>
            <div className="text-lg font-semibold text-[#5652B0]">
              {inventory.total}
            </div>
          </div>
          <div className="bg-[#F0FDF4] border border-[#DCFCE7] p-3 rounded-md shadow-sm">
            <div className="text-sm text-green-700">Available</div>
            <div className="text-lg font-semibold text-green-700">
              {inventory.available}
            </div>
          </div>
          <div className="bg-[#FFFBEB] border border-[#FEF3C7] p-3 rounded-md shadow-sm">
            <div className="text-sm text-amber-600">Reserved</div>
            <div className="text-lg font-semibold text-amber-600">
              {inventory.reserved}
            </div>
          </div>
          <div className="bg-[#FEF2F2] border border-[#FEE2E2] p-3 rounded-md shadow-sm">
            <div className="text-sm text-red-600">Damaged</div>
            <div className="text-lg font-semibold text-red-600">
              {inventory.damaged}
            </div>
          </div>
        </div>
      </div>

      {/* Batch Details */}
      <div className="p-4 bg-[#F9FAFB] border border-gray-200 rounded-md shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-100">
          Batch Details
        </h4>
        <TableContainer className="border border-gray-200 rounded-md bg-white">
          <Table size="small">
            <TableHead className="bg-[#F9F8FE]">
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: "Outfit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: "1px solid #E5E7EB",
                    color: "#4B5563",
                  }}>
                  Batch ID
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontFamily: "Outfit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: "1px solid #E5E7EB",
                    color: "#4B5563",
                  }}>
                  Quantity
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: "Outfit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: "1px solid #E5E7EB",
                    color: "#4B5563",
                  }}>
                  Manufacturing Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.batches.map(batch => (
                <TableRow
                  key={batch.id}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: 0,
                    },
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5,
                    }}>
                    {batch.id}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5,
                    }}>
                    {batch.quantity}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5,
                    }}>
                    {batch.manufacturingDate}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Warehouse Distribution */}
      <div className="p-4 bg-[#F9FAFB] border border-gray-200 rounded-md shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-100">
          Warehouse Distribution
        </h4>
        <TableContainer className="border border-gray-200 rounded-md bg-white">
          <Table size="small">
            <TableHead className="bg-[#F9F8FE]">
              <TableRow>
                <TableCell
                  sx={{
                    fontFamily: "Outfit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: "1px solid #E5E7EB",
                    color: "#4B5563",
                  }}>
                  Warehouse
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontFamily: "Outfit",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    borderBottom: "1px solid #E5E7EB",
                    color: "#4B5563",
                  }}>
                  Quantity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.warehouses.map((warehouse, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: 0,
                    },
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5,
                    }}>
                    {warehouse.name}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom: "1px solid #E5E7EB",
                      py: 1.5,
                    }}>
                    {warehouse.quantity}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-md p-5 border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-semibold text-gray-800">
          Variants & Inventory
        </h2>
        <button
          className="px-3 py-1.5 text-[#5652B0] hover:bg-[#F9F8FE] border border-[#5652B0] rounded-md flex items-center gap-1.5"
          onClick={() => infoToast("Edit not available in demo.")}>
          <Edit size={15} />
          <span className="text-sm font-medium">Edit</span>
        </button>
      </div>

      <TableContainer className="border border-gray-200 rounded-md shadow-sm">
        <Table>
          <TableHead className="bg-[#F9FAFB]">
            <TableRow>
              <TableCell width={60}></TableCell>
              <TableCell
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                Size
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                Color
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                MRP (₹)
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                Discount
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                Selling Price (₹)
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                Inventory
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  borderBottom: "1px solid #E5E7EB",
                  color: "#4B5563",
                }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variants.map(variant => (
              <React.Fragment key={variant.id}>
                <TableRow
                  sx={{
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  <TableCell
                    sx={{
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() =>
                        setExpandedRow(
                          expandedRow === variant.id ? null : variant.id,
                        )
                      }>
                      {expandedRow === variant.id ? (
                        <ChevronUp size={18} className="text-gray-600" />
                      ) : (
                        <ChevronDown size={18} className="text-gray-600" />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    {variant.size}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{
                          backgroundColor:
                            variant.color === "Default"
                              ? "#C4A484"
                              : variant.color.toLowerCase(),
                        }}></span>
                      {variant.color}
                    </div>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    <span className="text-gray-400 line-through">
                      {variant.mrp}
                    </span>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    <span className="text-green-600">{variant.discount}%</span>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                      color: "#5652B0",
                    }}>
                    {variant.sellingPrice}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    <div
                      className={`font-semibold ${
                        variant.inventory.available < 5
                          ? "text-amber-600"
                          : "text-gray-700"
                      }`}>
                      {variant.inventory.available} / {variant.inventory.total}
                    </div>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: "Outfit",
                      borderBottom:
                        expandedRow === variant.id
                          ? "none"
                          : "1px solid #E5E7EB",
                    }}>
                    {variant.inventory.available < 5 ? (
                      <div className="inline-flex items-center px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                        <AlertTriangle
                          size={12}
                          className="mr-1 text-amber-600"
                        />
                        Low Stock
                      </div>
                    ) : (
                      <div className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                        In Stock
                      </div>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={8}>
                    <Collapse
                      in={expandedRow === variant.id}
                      timeout="auto"
                      unmountOnExit>
                      {renderInventoryDetails(variant.inventory)}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default ProductVariantsInventory;
